// Desc: Terms and Conditions page component

import { ConfigurationAtom } from "_states";
import { useRecoilValue } from "recoil";

/**
 * TermsConditionsPage component
 * @returns {JSX.Element} JSX.Element
 */
const TermsConditionsPage = () => {
  const configurations = useRecoilValue(ConfigurationAtom);
  return (
    <section className="max-w-7xl mx-auto space-y-8 pb-36 md:px-2 sm:px-4 px-4">
      <div>
        <h2 className="font-semibold text-4xl text-center mb-10">
          Terms & Conditions
        </h2>
        <div className="space-y-4">
          <p className="has-normal-font-size">
            <strong>Important</strong>
          </p>

          <p className="has-normal-font-size">
            <strong>
              Please read the following “Booking &amp; Advisory Services – Terms
              and Conditions” (Booking T&amp;Cs) carefully as they contain
              important information about your legal rights, remedies and
              obligations.
            </strong>
          </p>

          <p className="has-small-font-size">
            This agreement is between you and {configurations.site_name}.
          </p>

          <p className="has-small-font-size">
            By making any booking for Travel Products with Us, you agree to
            comply with and be bound by these Booking T&amp;CS, which apply to
            any bookings you make with our consultants over the phone or by
            email as well as online bookings you make on our website.
          </p>

          <p className="has-small-font-size">
            We may modify our terms and conditions without notice at any time
            where such amendment does not substantially affect your rights and
            obligations. By using our site to search for and/or book the
            products and/or services offered, you are indicating:
          </p>

          <p className="has-small-font-size">
            – that you have read this User Agreement; and
          </p>

          <p className="has-small-font-size">
            – your acceptance of this User Agreement
          </p>

          <p className="has-small-font-size">
            Do not make any bookings unless you understand and agree with the
            following Booking T&amp;Cs and any other terms and conditions of the
            Travel Provider relating to the Travel Products you buy from us
            (Travel Provider T&amp;Cs). You should read all of the relevant
            Travel Provider T&amp;Cs on their relevant website/communications
            means before acquiring Booking &amp; Advisory Services from us.
          </p>

          <p></p>

          <p>
            <strong>1. Definitions</strong>
          </p>

          <p className="has-small-font-size">
            <strong>“We”</strong> and <strong>“us” </strong>means{" "}
            {configurations.site_name}
          </p>

          <p className="has-small-font-size">
            <strong>“You”</strong> or <strong>“your” </strong>means any user of
            our Website or any person who acquires the Booking &amp; Advisory
            Services, including any person who acquires a Travel Product
            (whether or not the Booking &amp; Advisory Services were acquired by
            you on behalf of another person or by another person on behalf of
            you).
          </p>

          <p className="has-small-font-size">
            <strong>“Booking &amp; Advisory Services” </strong>means services
            provided by us to you to acquire a Travel Product from a Travel
            Provider and includes advisory and consulting services and
            facilitation of your purchase from Travel Providers.
          </p>

          <p className="has-small-font-size">
            <strong>“Travel Provider”</strong> or{" "}
            <strong>“Travel Providers” </strong>means the company or person who
            provides you with the Travel Products such as airlines, tour and
            cruise operators, car hires and accommodation providers. This may
            also include ticket consolidators and wholesalers that we use to
            facilitate travel with the Travel Provider.
          </p>

          <p className="has-small-font-size">
            <strong>“Travel Product” </strong>means the service or product
            provided by a Travel Provider, such as flights, accommodation and
            ground transport.
          </p>

          <p></p>

          <p>
            <strong>2. General</strong>
          </p>

          <p className="has-small-font-size">
            a) We act as an <strong>agent</strong> in making the Arrangements
            with third party suppliers who provide Travel Products as principals
            (“Suppliers”) in providing access to facilitate flight bookings.
            Flight fulfilment is provided by the airline(s) selected at the time
            of finalizing the booking. For your air travel,
            <strong>
              {" "}
              you are bound by the terms and conditions and the fare rules of
              the selected airline(s)
            </strong>
            .
          </p>

          <p className="has-small-font-size">
            b) You acknowledge and agree that we may receive a commission or an
            affiliate fee or a marketing fee from transactions entered by using
            the Services.
          </p>

          <p className="has-small-font-size">
            You grant us the right to provide your contact details to airlines
            for the purpose of airlines (or their agents) providing operational
            notifications or updates in connection with your flights.
          </p>

          <p></p>

          <p>
            <strong>3. Our Services</strong>
          </p>

          <p>
            <strong>3.1 Agency</strong>
          </p>

          <p className="has-small-font-size">
            a) We provide a travel booking service that enables you to:
          </p>

          <ol className="wp-block-list" type="1">
            <li className="has-small-font-size">
              Book or purchase travel related goods and services promoted on our
              Website;
            </li>

            <li className="has-small-font-size">
              Conduct flight and other travel related searches and reservations;
            </li>

            <li className="has-small-font-size">
              Access our Customer Service assistance service;
            </li>

            <li className="has-small-font-size">
              Take advantage of our DEALS (special negotiated rates);
            </li>

            <li className="has-small-font-size">
              Use our payment processing, documentation and ticketing services.
            </li>
          </ol>

          <p className="has-small-font-size">
            b) Your legal rights and remedies in connection with the provision
            of the Travel Products you book are against the Supplier and, except
            to the extent a problem is directly and primarily caused by fault on
            our part, are not against us. Specifically, if for any reason
            (excluding fault on our part) any Supplier is unable to provide the
            Travel Product for which you have contracted either at all, or to
            the requisite standard, your remedies are against that Supplier and
            not against us. However, our Customer Service team will assist you
            should you have an issue with a Supplier or the Travel Products and
            services they provide to you.
          </p>

          <p>
            <strong>3.2 Customer Service</strong>
          </p>

          <p className="has-small-font-size">
            The Customer Service provided includes access to staff either
            online, by email or by phone to confirm your Booking, make any
            changes to your Booking or otherwise seek assistance with your
            Booking. The Customer Service provided by us is global in nature and
            can be accessed from anywhere around the world.
          </p>

          <p className="has-small-font-size">
            Phone: {configurations.contact_number}
          </p>

          <p className="has-small-font-size">
            Email: {configurations.contact_email}
          </p>

          <p></p>

          <p>
            <strong>4. Travel Products</strong>
          </p>

          <p>
            <strong>4.1 Product Terms</strong>
          </p>

          <p className="has-small-font-size">
            In addition to these terms, Suppliers and hotels may impose (and you
            must comply with)&nbsp;their own terms and conditions in relation to
            their Travel Products (Product Terms).&nbsp;If requested, we can
            provide you with copies of the relevant travel service provider
            terms and conditions.
          </p>

          <p className="has-small-font-size">
            <strong>4.2 Flights</strong>
            <br />
            <br />
            Flight fulfilment is provided by the airline(s) selected at the time
            of finalizing the Booking. For your air travel, you are bound by the
            Product Terms of the selected airline(s).
          </p>

          <p className="has-small-font-size">
            We are not responsible for subsequent changes to airline scheduled
            departure times, departure dates or flight cancellations.
          </p>

          <p>
            <strong>4.3 Flights Deals</strong>
          </p>

          <p className="has-small-font-size">
            a) We negotiate with various airlines to provide deals on flights.
          </p>

          <p className="has-small-font-size">
            b) Ticket Numbers will be generated between{" "}
            <strong>7 days prior to departure</strong>. No changes are permitted
            within 7 days of departure.
          </p>

          <p>
            <strong>4.4 Packages</strong>
          </p>

          <p className="has-small-font-size">
            Packaged holidays (Packages) are made available by us by combining
            various suppliers.
          </p>

          <p className="has-small-font-size">You agree:</p>

          <p className="has-small-font-size">
            a) to comply with the applicable Product Terms for each of the
            Travel Products; and
          </p>

          <p className="has-small-font-size">
            b) that any further charges relating to your Booking (for example,
            additional bedding, locally payable taxes, resort fees, minibar
            charges, and any transfer costs and meals not included in the
            Package charges) will be invoiced by, and paid by you to, the
            applicable hotel or Supplier.
          </p>

          <p className="has-small-font-size">
            We are not authorised to disclose the component pricing of the
            packaged Travel Products.
          </p>

          <p></p>

          <p>
            <strong>5. Fees &amp; Commission</strong>
          </p>

          <p>
            <strong>5.1&nbsp;Fees</strong>
          </p>

          <p className="has-small-font-size">
            All fees charged by us are in addition to any Fees or non-refundable
            components charged by the Travel Providers (As details in the
            relevant Travel Provider T&amp;Cs).{" "}
          </p>

          <p>
            <strong>5.2&nbsp;Commissions</strong>
          </p>

          <p className="has-small-font-size">
            In addition to the Booking &amp; Advisory fee, we may receive
            commission, fees, gifts or financial incentives from Travel
            Providers in respect of your booking.
          </p>

          <p>
            <strong>5.3&nbsp;How are funds held ?</strong>
          </p>

          <p className="has-small-font-size">
            a)&nbsp;&nbsp;Any funds received by you, will be held on trust until
            such time as remitted to the Travel Provider. A portion of your
            payment may include a commission component that we will retain on
            trust until your travel is taken.
          </p>

          <p className="has-small-font-size">
            b)&nbsp;&nbsp;Where we charge you a service fee or other fee
            detailed in our schedule of Fees, this will not be held on trust as
            these amounts are non-refundable.
          </p>

          <p></p>

          <p>
            <strong>6. Booking Terms</strong>
          </p>

          <ol className="wp-block-list" type="1">
            <li className="has-small-font-size">
              The Travel Products offered are subject to availability and can be
              withdrawn without notice by the Travel Provider. Travel Products
              may also change at any time in accordance with the terms and
              conditions you agree with the Third-Party Travel Provider.
            </li>
          </ol>

          <ol className="wp-block-list" type="1" start={2}>
            <li className="has-small-font-size">
              When you make a booking, you must provide accurate and complete
              details of each traveller, and according to their passport or
              other identification document. It is your responsibility to ensure
              that all details of the booking are complete and correct for all
              passengers before proceeding. Some Third-Party Travel Providers
              will deny carriage if the traveller’s name varies from their
              booking and may cancel automatically if the traveller’s name is
              amended. We have no responsibility for any loss or damage arising
              from the incorrect entry of a traveller’s name or as a result of
              the Third-Party Travel Provider’s policies.
            </li>
          </ol>

          <ol className="wp-block-list" type="1" start={3}>
            <li className="has-small-font-size">
              It is your responsibility to ensure that all details of the
              booking are correct before proceeding.
              <br />
            </li>

            <li className="has-small-font-size">
              Travel Products are not guaranteed until payment has been made in
              full and documents have been processed.
            </li>
          </ol>

          <ol className="wp-block-list" type="1" start={5}>
            <li className="has-small-font-size">
              Most airlines offer only electronic confirmation of your
              reservation, or ‘e-ticketing’, on certain routes. We cannot be
              held responsible if your e-ticket does not arrive due to an
              incorrect email address or your junk email settings. You must
              notify us immediately if you change your email address or contact
              telephone number after making a booking. It is your responsibility
              to advise us if you have not received your e-ticket confirmation
              (refer to Customer Service 3.2 for contact details).
            </li>
          </ol>

          <p></p>

          <p>
            <strong>7. Payment Terms</strong>
          </p>

          <p>
            <strong>7.1 Deposits</strong>
          </p>

          <p className="has-small-font-size">
            a) You will be required to pay an advisory service fees as per Fees
            5.1 as deposit or deposits for your booking. In some instances, full
            payment is required at the time of booking. We will advise you of
            these payment requirements.
          </p>

          <p className="has-small-font-size">
            b) <strong>All deposits are non-refundable</strong> for changes of
            mind or cancellation by you.
          </p>

          <p className="has-small-font-size">
            c) Upto 20/11/2023, Deposits were forfeited&nbsp;after
            3&nbsp;months&nbsp;if not utilized from the date of transaction.
          </p>

          <p className="has-small-font-size">
            d) From 21/11/2023 to 25/06/2024, Deposits will be forfeited after
            6&nbsp;months&nbsp;if not utilized from the date of transaction.
          </p>

          <p>
            e) On &amp; from 26/06/2024, Deposits will be forfeited after
            12&nbsp;months&nbsp;if not utilized from the date of transaction.
          </p>

          <p>
            <strong>7.2 Final Payment of balance due</strong>
          </p>

          <p className="has-small-font-size">
            a) Final payment is required no later than 12 hours from the time of
            making the booking, unless otherwise stated.
          </p>

          <p className="has-small-font-size">
            b) Any failure by you to make payment of the Travel Product in full
            by the due date may result in your booking being cancelled and the
            forfeiture of any deposit you have made.
          </p>

          <p>
            <strong>7.3 Currency</strong>
          </p>

          <p className="has-small-font-size">
            a) Unless otherwise stated, payments are in Indian rupees.
          </p>

          <p className="has-small-font-size">
            b) All pricing is inclusive of goods and services taxes (GST), or
            other such value added taxes, where applicable. Other taxes may
            apply upon Check- in.
          </p>

          <p className="has-small-font-size">
            c) International Airfares do not have a GST component.
          </p>

          <p>
            <strong>7.4 Payment Methods</strong>
          </p>

          <p className="has-small-font-size">
            We accept the various popular payment methods.
          </p>

          <p className="has-small-font-size">
            Some medium of payments will require up to two (2) days to clear and
            appear in our account.
          </p>

          <p></p>

          <p>
            <strong>
              8. Changes in Price and Itineraries for Travel Products
            </strong>
          </p>

          <ol className="wp-block-list" type="1">
            <li className="has-small-font-size">
              Prices for Travel Products displayed on our Website or otherwise
              communicated to you are provided as an indicative guide only and
              may vary due to peak demand periods, changes in surcharges, fees
              or taxes or currency fluctuations.
            </li>
          </ol>

          <ol className="wp-block-list" type="1" start={2}>
            <li className="has-small-font-size">
              You should check all prices for Travel Products with us before
              acquiring Booking &amp; Advisory Services. The Price of the Travel
              Products is not fixed until you receive confirmation from us that
              your booking is confirmed.
            </li>
          </ol>

          <ol className="wp-block-list" type="1" start={3}>
            <li className="has-small-font-size">
              In the event that the Travel Provider adds surcharges, fees or
              taxes in relation to your booking after you have booked your
              Travel Products with us, we will advise you of the proposed price
              increases. If you do not wish to incur the additional charges, you
              may cancel the Travel Product.
              <br />
            </li>

            <li>
              In case of an obvious error (including any error in electronic
              labelling, error in data entry, error of calculation or clerical
              error) resulting in the display of a price which has obviously
              been displayed by mistake, we reserve the right to cancel your
              reservation, even if it was automatically confirmed on our
              Website. You will be informed of this as soon as possible, to
              allow you to make a reservation at the correct price, if you wish
              to. In any case, you will not be entitled to claim compensation
              from us on the grounds of the cancellation or the related costs,
              if any, that have been incurred due to an erroneous reservation
              which has been cancelled.
            </li>
          </ol>

          <ol className="wp-block-list" type="1" start={5}>
            <li>
              We reserve the right to cancel your booking in the event we have
              reasonable grounds to believe it is fraudulent. In such
              circumstances we will attempt to contact you using the email
              address you provided at the time of booking or your bank. In the
              event we are not able to contact you or your bank, we may cancel
              your booking with no liability whatsoever. In the unlikely event
              that your booking has to be cancelled, a full refund will be made
              of all monies previously paid to us to the person(s) who
              originally paid for the booking.
            </li>
          </ol>

          <ol className="wp-block-list" type="1" start={6}>
            <li>
              If a Third-Party Travel Provider changes any part of your booking
              for reasons beyond its control, for example, if a Third-Party
              Travel Provider changes its schedules, overbooks, or if there are
              any changes in applicable surcharges, fees or taxes, we will use
              our reasonable endeavours to notify you. If any such changes
              result in your Travel Product costing more or otherwise being
              materially different, then you may cancel the Travel Product and
              we will refund any monies already paid less any fees charged by us
              under these terms and conditions and by the Third-Party Travel
              Providers under the terms and conditions you agreed with them.
            </li>
          </ol>

          <ol className="wp-block-list" type="1" start={7}>
            <li>
              It is your responsibility to re-confirm all flight departure times
              with the relevant airline/s prior to departure. Whilst we will
              endeavour to send e-mail notification of any changes to the
              original itinerary, we will not be liable for any costs incurred
              as a result of passengers missing any flight due to your failure
              to check your itinerary prior to departure.
            </li>
          </ol>

          <p>
            <strong>9. Amendments and Cancellations</strong>
          </p>

          <p>
            <strong>9.1 Cancellations</strong>
          </p>

          <p>
            a) If you cancel your Travel Product, your right to a refund is
            subject to relevant Travel Provider T&amp;Cs. It is not within our
            power to decide whether you will be eligible for a refund for the
            cost of the Travel Product from the Travel Provider.
          </p>

          <p>
            b) Cancellation and Refund can only be processed on filling the
            refund form on our website. No other medium is accepted to apply for
            refund.
          </p>

          <p>
            c) Applicable only for Flight Deals: If you have booked our Flight
            Deals with more than one passenger, the bookings can only be
            cancelled as a whole booking. You will then have to cancel your
            group booking and then re-book your flights. The money that you have
            paid will be adjusted to the new bookings based on availability and
            price.
          </p>

          <p>
            <strong>9.2 Fees</strong>
          </p>

          <p>
            a)&nbsp;If you cancel you Travel Product, or amendments to your
            booking are otherwise required, we charge cancellation and amendment
            fees as per the Schedule of Fees. These fees reflect the work
            required to cancel the Travel Product and in the circumstances,
            reflects our best pre-estimate of the costs that we will incur
            managing this process. However, we will not charge a cancellation or
            amendment fee if you cancel or amend your booking due to a breach by
            us of our obligations to you or if the cancellation or amendment is
            required due to our error.
          </p>

          <p>
            b) The Travel Provider may also charge cancellation and amendment
            fees in accordance with the relevant Travel Provider T&amp;Cs. These
            fees will be in addition to the fees charged by Us. We are not
            liable to you for any cancellation fees, amendment fees or refusals
            made by the Travel Provider.
          </p>

          <p>
            <strong>
              9.3 We reserve the right to cancel your booking in the event we
              have reasonable grounds to believe it is fraudulent. In such
              circumstances, we will attempt to contact you using the email
              address you provided at the time of booking, or via your bank. In
              the event we are not able to contact you or your bank, we may
              cancel your booking with no liability whatsoever, subject to the
              relevant Travel Provider T&amp;Cs, a full refund will be made of
              all the monies previously paid to us to the person(s) who
              originally paid for the booking(less any fees charged by us under
              these Booking T&amp;Cs)
            </strong>
          </p>

          <p></p>

          <p>
            <strong>10. Refunds</strong>
            <strong></strong>
          </p>

          <p>
            10.1 Your entitlement to any refund will depend on the relevant
            Travel Provider T&amp;Cs.
          </p>

          <p>
            10.2 We strongly recommend that you read and understand these
            Booking T&amp;Cs and all relevant Travel Provider T&amp;Cs, before
            making the booking. Your booking may be non-refundable.
          </p>

          <p>
            10.3 As you have booked through us, you should contact us in the
            first instance to discuss your rights and any request for a refund.
          </p>

          <p>
            10.4 If your refund request is granted by the Travel Provider, the
            amount of refund will be the amount paid less and any fees charged
            by us and fees charged by the Travel Provider in accordance with the
            relevant Travel provider T&amp;Cs.
          </p>

          <p>
            10.5 If the Travel Provider is required to or otherwise agrees to
            provide you with a refund for the Travel Product, we will facilitate
            the refund between the Travel Provider and you. Once we have
            received the refund from the Travel Provider, we will pass it on to
            you as soon as practicable. Refund processing could take minimum six
            (6) months or more depending upon the Travel Provider.
          </p>

          <p>
            10.6 No refund is provided on partially utilised tickets. Refunds
            due to medical reasons are very rarely given by the airlines as such
            Travel Insurance is highly recommended.
          </p>

          <p>
            10.7{" "}
            <strong>
              We shall only be liable to provide a refund to you to the extent
              that it actually receives a refund from the relevant Third Party
              Provider and to the extent that such a refund from the relevant
              Third Party Provider exceeds our cancellation fees.
            </strong>
          </p>

          <p>
            10.8 In a situation where an error is incurred by us then we shall
            only be liable to provide a refund to you to the extent of the value
            of the Travel Product that you have purchased from us (for example,
            you will only receive refund of the ticket value that you have
            purchased through us).
          </p>

          <p>
            10.9 If you have a booking with us in relation to a Travel but you
            do not show up to check-in or otherwise do not avail yourself of the
            Travel Product, you will not be entitled to any refund from us.
          </p>

          <p></p>

          <p>
            <strong>11. Liability </strong>
            <strong></strong>
          </p>

          <p>
            <strong>
              11.1 Liability of Agency and Limitation of Liability
            </strong>
          </p>

          <p>
            a) Your legal rights in connection with our provision of Booking and
            Advisory Services&nbsp;are against us.
          </p>

          <p>
            b) We are not liable to you or anyone else (including for
            negligence, breach of contract or tort) for any loss or damage
            (including specific, direct, indirect, consequential, economic loss,
            incidental damages, lost profits or savings or damages for
            disappointment) which is suffered directly or indirectly in
            connection with:
          </p>

          <p>
            i) any failure or delay on the part of the Travel Provider or any
            third party in relation to the supply of Travel Products to you;
          </p>

          <p>
            ii) any acts or omissions of Travel Providers or third parties in
            the course of supplying Travel Products (including any liability in
            contract, tort, or otherwise, for any direct, indirect
            consequential, economic loss, incidental damages, lost profits or
            savings or damages for disappointment);
          </p>

          <p>
            iii) any technical errors, corruption of any data, unauthorized
            access to your personal data, inaccuracies in information supplied
            by third parties, or failure to complete bookings when that failure
            is due to circumstances beyond our control;
          </p>

          <p>
            iv) use of (or inability to use) our website or any linked website;
          </p>

          <p>v) disruption to our website;</p>

          <p>vi) the delivery or non-delivery of the Travel Product or;</p>

          <p>
            vii) any act or omission of Travel Providers or other third parties;
          </p>

          <p>viii) disruption to our phone lines.</p>

          <p>
            (c) Your rights with respect to a confirmed booking affected by an
            event of Force Majeure will be subject to the terms and conditions
            of the relevant Supplier.
          </p>

          <p>
            <strong>11.2&nbsp;Liability of Travel Provider</strong>
          </p>

          <p>
            a) Your legal rights in connection with the provision of the Travel
            Product are against the Travel Provider.
          </p>

          <p>
            b) You pay the Travel Provider (via us the agent) for providing the
            Travel Product to you. Once you have acquired Booking &amp; Advisory
            Services from us, the Travel Provider will provide you with the
            Travel Product on the relevant Travel Provider TnCs.
          </p>

          <p>
            c) The Travel Provider is the supplier of the Travel Products and is
            responsible for any express or implied guarantee in relation to the
            supply of Travel Products.
          </p>

          <p>
            d) All information, including but not limited to representations
            that a Travel Provider makes on its brochures, relating to the
            Travel Provider or a Travel Product is provided by the Travel
            Providers or other independent third parties. We are not including
            the standard, class, or description of accommodation or service
            provided by Travel Providers.
          </p>

          <p>
            e) Without limiting the disclaimer of liability in the previous
            paragraph, any obligation we have to you will be suspended during
            the time and to the extent that we are prevented from, or delayed
            in, complying with that obligation by an event of Force Majeure.
          </p>

          <p>
            <strong>11.3 Authority</strong>
          </p>

          <p>
            a) You are solely responsible for the suitability of any Travel
            Product that you purchase. We are not liable for any inconvenience
            caused or expense incurred as a result of any unsuitability of the
            Travel Product.
          </p>

          <p>
            b) We will rely on the authority of the person making the booking to
            act on behalf of any other traveller on the booking (regardless of
            whether other travellers have made separate payments) and that
            person will bind all such travellers to these Booking T7Cc and to
            the other Travel Provider T&amp;Cs.
          </p>

          <p>
            <strong>11.4 Indemnification</strong>
          </p>

          <p>
            Except to the extent caused by our fraud, negligence or willful
            neglect, you agree to indemnify us and any of our officers,
            directors, employees and agents from and against any claims, causes
            of action, demands, recoveries, losses, damages, fines, penalties or
            other costs or expenses of any kind or nature including but not
            limited to reasonable legal and accounting fees, brought by any
            third parties as a result of:
          </p>

          <p>
            a) your breach of these Terms and Conditions or the documents
            referenced herein.
          </p>

          <p>b) your breach of any law or the rights of a third party; or</p>

          <p>c) your use of this Website.</p>

          <p></p>

          <p>
            <strong>12. Force Majeure</strong>
          </p>

          <p>
            12.1 We will not be liable for any failure or delay in providing you
            with Booking &amp; Advisory Services in relation to Travel Products
            that is due to events beyond our control, including but not limited
            to acts of God, accident, riot, war, terrorist act, epidemic,
            pandemic, quarantine, outbreaks of infectious disease or any other
            public health crisis, civil commotion, breakdown of communication
            facilities, natural catastrophes, governmental acts or omissions,
            changes in laws or regulations, national strikes, fire, explosion,
            generalised lack of availability of raw materials or energy (force
            majeure event).
          </p>

          <p>
            12.2 For the avoidance of any doubt, a force majeure event shall not
            include:
          </p>

          <p>
            a) your (including third parties you may be booking for) financial
            distress nor the inability of you (including third parties you may
            be booking for) or us to make a profit or avoid a financial loss;
          </p>

          <p>b) changes in market prices or conditions, or;</p>

          <p>
            c) your (including third parties you may be booking for) financial
            inability to perform you (including third parties you may be booking
            for) obligations hereunder.
          </p>

          <p>
            12.3 If a force majeure event occurs that affects your booking, your
            entitlement to a refund, credit or re scheduled travel bookings,
            will depend on the relevant Travel Provider T&amp;Cs.
          </p>

          <p>
            12.4 If your booking is impacted by a force majeure event and your
            entitled to a refund from the Travel Provider, we will facilitate
            this refund to you as soon as reasonably practicable.
          </p>

          <p>
            12.5 For the avoidance if any doubt, in the circumstance that a
            force majeure event occurs the following fees are non-refundable.
          </p>

          <p>
            a)&nbsp;&nbsp;The service fees paid to us in respect of your
            booking.
          </p>

          <p>b) &nbsp;Any other fees referred to in our Schedule of Fees.</p>

          <p></p>

          <p>
            <strong>13. Your Obligations and Warranties</strong>
          </p>

          <p>13.1 You warrant and acknowledge to us that</p>

          <p>
            a) You are at least eighteen (18) years old and have the power,
            capacity, and authority to enter into a binding contract with us and
            the Provider.
          </p>

          <p>
            b) You have sufficient funds to pay for the Booking &amp; Advisory
            Services and the Travel Product.
          </p>

          <p>
            c) You have read and understood these Booking T&amp;Cs and if
            booking on behalf of third parties, you understand that their
            booking is subject to these Booking T&amp;Cs and all relevant Travel
            Provider T&amp;Cs.
          </p>

          <p>
            d) The information you provide us about yourself and third parties
            is true, accurate, current and complete (apart from any optional
            items) as required by any registration process.
          </p>

          <p>
            e) You will maintain and promptly update this information to keep it
            true, accurate and complete.
          </p>

          <p>
            f) You have considered acquiring comprehensive travel insurance and
            we are not responsible for any failure by you to acquire adequate
            insurance cover.
          </p>

          <p>
            g) You will use the Booking &amp; Advisory Services in accordance
            with these Booking T&amp;Cs and you will not use the Booking &amp;
            Advisory Services in any way to breach any laws or defame any
            person.
          </p>

          <p>
            h) You will be to the airport at least 3 hours prior to your flight
            departure.
          </p>

          <p></p>

          <p>
            <strong>14. Passports, Visas and Health Requirements</strong>
          </p>

          <p>
            <strong>14.1 Passports&nbsp;</strong>
          </p>

          <p>
            (a) All departing passengers require a valid passport for
            international travel. This means the passport must be valid for a
            set minimum period beyond your travel dates (the required minimum
            period depends on the country you visit). &nbsp;It is your
            responsibility to ensure that you have at least six (6) months
            validity on your passport from the date you return from your
            departure.
          </p>

          <p>
            (b){" "}
            <strong>
              Please ensure you have a valid Passport well in advance of travel
              as either the airline staff or immigration authorities will not
              allow you to pass through customs and board your flight without
              this documentation.
            </strong>
          </p>

          <p>
            (d) It is your responsibility to ensure that you have the required
            documents including passports and visas, before travelling to a
            destination. It is your responsibility to check with the respective
            Embassy or Consulate of each country that you are travelling to. It
            is your responsibility to ensure that you have at least six (6)
            months validity on your passport from the date you return from your
            departure.
          </p>

          <p>
            <strong>14.2 Visas&nbsp;</strong>
          </p>

          <p>
            (a) A visa provides you with the authority as a non-citizen to
            travel to another country. Some countries will not allow you to
            enter without a visa, or in some cases, without proof of a return or
            onward electronic or paper ticket.
          </p>

          <p>
            (b){" "}
            <strong>
              Please ensure you have a valid Visa (as applicable) for all the
              countries you are travelling to which require a visa well in
              advance of travel as either the airline staff or immigration
              authorities will not allow you to pass through customs and board
              your flight without this documentation.
            </strong>
          </p>

          <p>
            (c) Visas are obtained from the issuing authority, usually an
            embassy. Visa requirements vary by both country you are travelling
            to and the current citizenship you hold so please ensure you check
            well in advance of your travel dates to allow sufficient processing
            time as required by the embassy. If you have booked a one-way ticket
            please ensure you have checked with the Consulate of the country you
            are travelling to in order to ensure that you comply with their laws
            and regulations.
          </p>

          <p></p>

          <p>
            <strong>14.3 General</strong>
          </p>

          <p>
            (a) We recommend before travelling that you check the Government
            website relating to the countries you are travelling to for any
            applicable travel advisories relevant to those countries.
          </p>

          <p>
            (b) If any Covid related restrictions are imposed to the countries
            you are travelling, we are not liable if you are unable to travel or
            denied boarding for any reason including visas/passport/ vaccination
            certificate/ Air Suvidha forms/ RT PCR tests certificate not in
            order.
          </p>

          <p>
            (c) To assist you, in determining the travel restrictions in
            relation to COVID imposed by countries please visit out Travel
            restriction page on governmental websites.
          </p>

          <p>
            (d) As well, please note that Government legislation prohibits the
            carriage of dangerous goods on aircraft.
          </p>

          <p></p>

          <p>
            <strong>15. Travel Insurance</strong>
          </p>

          <p>
            15.1 Your safety and enjoyment is important to us and we strongly
            recommend that you and any third parties you may be booking for
            obtain an appropriate travel insurance policy. A travel insurance
            policy may help cover any costs associated with cancellations,
            medical and repatriation expenses, personal injuries or accidents,
            death, loss of personal baggage or money, and personal liability
            insurance.
          </p>

          <p>
            15.2 It is your responsibility to ensure that you are adequately
            protected by an appropriate travel insurance policy.
          </p>

          <p></p>

          <p>
            <strong>16. Website and Links</strong>
            <strong></strong>
          </p>

          <p>
            16.1 We may at any time modify these terms and conditions and the
            changes will come into effect once they are uploaded onto our
            Website.
          </p>

          <p>
            16.2 The applicable terms that apply to you are the Booking T&amp;Cs
            that were in effect at the time you made your booking with us.
          </p>

          <p>
            16.3 You acknowledge that the inclusion of links on our Website is
            not intended as an endorsement or recommendation of any linked
            website or its content of such sites. We do not guarantee that our
            Website or any linked website will be free from viruses, or that our
            Website or any linked website will be uninterrupted.
          </p>

          <p>
            16.4 We reserve the right to correct any errors in rates or content
            quoted or calculated for any Travel Product, with immediate effect.
            We will use reasonable endeavours to notify you of such corrections.
          </p>

          <p></p>

          <p>
            <strong>17 Privacy</strong>
            <strong></strong>
          </p>

          <p>
            17.1 You acknowledge and agree that we may disclose some or all of
            your personal information as outlined in our Privacy Policy. The
            terms of our Privacy policy are incorporated into these Booking
            T&amp;Cs.
          </p>

          <p></p>

          <p>
            <strong>18. Complaints or Problems</strong>
          </p>

          <p>
            We endeavor to ensure that the arrangements we have made for you are
            implemented as arranged. If a problem occurs, the most practical way
            to deal with it is to attempt resolution locally with the supplier.
          </p>

          <p>
            If you fail to follow this course, any claim for compensation may be
            reduced or denied. If you have unresolved complaint, details should
            be lodged in writing directly to the company within 30 days of the
            incidence.
          </p>

          <p>
            We have no liability (including for loss or damage) or any act,
            omission, or default, whether negligent or otherwise of any
            merchant. If you wish to make a complaint, then fill the complaint
            form via email.
            <br />
          </p>
        </div>
      </div>
    </section>
  );
};

export default TermsConditionsPage;
